import { useEffect, useMemo, useState, useCallback } from "react";
import { cachedGet } from "@/lib/apiCache";
import { toParams } from "@/lib/kpi";
import { Euro, Target, TrendingUp, TrendingDown, Percent, Calendar, Info, BarChart3 } from "lucide-react";
import CaDetailTable from "./CaDetailTable";
import { WeekdayWeight, WeeksWeight, Contributions, Day14h, ProjectionChart, ExtraStats, WeatherImpact } from "./CaDetailWidgets";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtEur, fmtEurS, fmtPct, fmtPctS, signCls, MONTHS_UP } from "./caDetailUtils";

const LS_KEY = "orbit_cad_filters";

function Kpi({ icon: Icon, tone = "blue", label, value, lines = [], progress, testid }) {
  const tones = { blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600", red: "bg-rose-50 text-rose-600", amber: "bg-amber-50 text-amber-600", indigo: "bg-indigo-50 text-indigo-600" };
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2.5 flex gap-2.5 shadow-[0_1px_3px_rgba(15,23,42,.04)] min-w-0" data-testid={testid}>
      <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${tones[tone]}`}><Icon className="w-3.5 h-3.5"/></div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold text-slate-500 truncate">{label}</div>
        <div className="text-[15px] font-bold text-slate-900 leading-tight tabular-nums truncate">{value}</div>
        {progress != null && <div className="h-1 rounded-full bg-slate-100 overflow-hidden my-1"><div className="h-full rounded-full" style={{ width: `${Math.min(100, progress * 100)}%`, background: progress >= 1 ? "#16A34A" : progress >= 0.95 ? "#F59E0B" : "#2563EB" }}/></div>}
        {lines.map((l, i) => <div key={i} className={`text-[10px] truncate ${l.cls || "text-slate-500"}`}>{l.text}</div>)}
      </div>
    </div>
  );
}

function ResteAFaire({ refm }) {
  if (!refm) return null;
  const ahead = refm.ecart >= 0;
  const pct = Math.min(1, refm.pct || 0);
  const projOk = refm.projection_pct >= 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cad-reste">
      <h2 className="font-display text-sm font-bold text-slate-900 mb-2">Reste à faire <span className="text-slate-400 font-normal">(mois de {refm.mois_label})</span></h2>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
        <div className="lg:col-span-5">
          <div className="text-[11px] text-slate-600 mb-1"><b className="text-slate-900">{fmtEur(refm.realise)}</b> réalisés sur <b className="text-slate-900">{fmtEur(refm.objectif)}</b></div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${pct >= 1 ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${pct * 100}%` }}/></div>
          <div className="text-[10px] text-slate-500 mt-1">{fmtPct(refm.pct)} de l'objectif · {refm.days_done} jour{refm.days_done > 1 ? "s" : ""} réalisé{refm.days_done > 1 ? "s" : ""}</div>
        </div>
        <div className="lg:col-span-2 text-center">
          <div className={`text-lg font-bold tabular-nums ${signCls(refm.ecart)}`} data-testid="cad-reste-ecart">{fmtEurS(refm.ecart)}</div>
          <div className="text-[10px] text-slate-500">{ahead ? "au-dessus de l'objectif" : "en dessous de l'objectif"}</div>
        </div>
        <div className="lg:col-span-1 text-center">
          <div className="text-lg font-bold text-slate-900" data-testid="cad-reste-jours">{refm.days_left}</div>
          <div className="text-[10px] text-slate-500">jour{refm.days_left > 1 ? "s" : ""} restant{refm.days_left > 1 ? "s" : ""}</div>
        </div>
        <div className="lg:col-span-2 text-center">
          <div className="text-[10px] text-slate-500">Projection fin de mois</div>
          <div className="text-lg font-bold text-slate-900 tabular-nums" data-testid="cad-reste-proj">{fmtEur(refm.projection)}</div>
          <div className={`text-[10px] font-semibold ${signCls(refm.projection_pct)}`}>{fmtPctS(refm.projection_pct)} vs objectif</div>
        </div>
        <div className={`lg:col-span-2 rounded-lg px-3 py-2 text-[11px] flex items-start gap-2 ${projOk ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`} data-testid="cad-reste-msg">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>{refm.days_left === 0
            ? (ahead ? "Mois terminé : objectif atteint." : "Mois terminé : objectif non atteint.")
            : projOk
              ? `Si le rythme actuel se maintient, l'objectif du mois sera atteint (${fmtEurS(refm.projection - refm.objectif)}).`
              : `En retard : il faut réaliser ${fmtEur(Math.max(0, refm.objectif - refm.realise) / Math.max(1, refm.days_left))}/jour sur les ${refm.days_left} jours restants pour atteindre l'objectif (rythme actuel ${fmtEur(refm.avg_day)}/jour).`}</span>
        </div>
      </div>
    </div>
  );
}

export default function CaDetailView({ meta, FilterBar }) {
  const [f, setF] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; } });
  const [defaultsSet, setDefaultsSet] = useState(false);
  const [group, setGroup] = useState("corner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const defaults = useCallback(() => {
    const years = meta?.annees || [];
    const y = years[years.length - 1];
    const last = (meta?.dates || []).filter(d => d.startsWith(String(y))).sort().pop();
    return { annees: y ? [y] : [], mois: last ? [MONTHS_UP[+last.slice(5, 7) - 1]] : [] };
  }, [meta]);

  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(f)); } catch {} }, [f]);
  useEffect(() => {
    if (defaultsSet || !meta) return;
    if (!(f.annees?.length) && !(f.mois?.length) && !(f.jours?.length)) setF(defaults());
    setDefaultsSet(true);
  }, [defaultsSet, meta, f, defaults]);

  const setFWithDefaults = useCallback((upd) => {
    setF(prev => { const next = typeof upd === "function" ? upd(prev) : upd; return Object.keys(next).length === 0 ? defaults() : next; });
  }, [defaults]);

  useEffect(() => {
    if (!defaultsSet) return;
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      cachedGet("/kpi/ca-daily/detail", { params: { ...toParams(f), group }, signal: controller.signal, ttl: 60_000 })
        .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
    }, 200);
    return () => { clearTimeout(t); controller.abort(); };
  }, [f, group, defaultsSet]);

  const s = data?.summary || {};
  const { user } = useAuth();
  const canSync = ["admin", "manager", "support", "controller"].includes(user?.role);
  const [impact, setImpact] = useState(null);
  const [wxStatus, setWxStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    if (!defaultsSet) return;
    cachedGet("/weather/impact", { params: toParams(f), ttl: 60_000 }).then(setImpact).catch(() => {});
  }, [f, defaultsSet, wxStatus?.running]);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/weather/sync/status").then(r => { if (alive) setWxStatus(r.data); }).catch(() => {});
    poll();
    const id = setInterval(() => { if (wxStatus?.running || !wxStatus) poll(); }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [wxStatus?.running]);
  const syncWeather = async () => {
    setSyncing(true);
    try { await api.post("/weather/sync"); toast.success("Récupération météo lancée en arrière-plan"); setWxStatus(w => ({ ...(w || {}), running: true })); }
    catch { toast.error("Impossible de lancer la récupération météo"); }
    finally { setSyncing(false); }
  };
  const ref = data?.ref_month;
  const rows = useMemo(() => data?.rows || [], [data]);

  return (
    <div className="space-y-4" data-testid="cad-view">
      <FilterBar meta={meta} f={f} setF={setFWithDefaults}/>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="cad-kpis">
        <Kpi testid="cad-kpi-realise" icon={Euro} label="CA réalisé" value={fmtEur(s.realise)}
             lines={[{ text: `${fmtPctS(s.evo_n1)} vs N-1`, cls: signCls(s.evo_n1) + " font-semibold" }, { text: `${fmtEurS(s.realise - s.n_1)} vs N-1` }]}/>
        <Kpi testid="cad-kpi-objectif" icon={Target} tone="indigo" label="Objectif" value={fmtEur(s.objectif)} progress={s.pct}
             lines={[{ text: `${fmtPct(s.pct)} réalisé` }]}/>
        <Kpi testid="cad-kpi-ecart" icon={s.ecart >= 0 ? TrendingUp : TrendingDown} tone={s.ecart >= 0 ? "green" : "red"} label="Écart" value={fmtEurS(s.ecart)}
             lines={[{ text: `${fmtPctS(s.pct != null ? s.pct - 1 : null)} vs objectif`, cls: signCls(s.ecart) + " font-semibold" }]}/>
        <Kpi testid="cad-kpi-n1" icon={Calendar} tone="amber" label="N-1" value={fmtEur(s.n_1)}
             lines={[{ text: `${fmtPctS(s.evo_n1)} évolution`, cls: signCls(s.evo_n1) + " font-semibold" }]}/>
        <Kpi testid="cad-kpi-lfl" icon={Percent} tone={s.lfl_pct >= 0 ? "green" : "red"} label="LFL" value={fmtPctS(s.lfl_pct)}
             lines={[{ text: `${fmtEurS(s.lfl_eur)} périmètre comparable`, cls: signCls(s.lfl_eur) + " font-semibold" }, { text: `${s.lfl_corners || 0} corners comparables` }]}/>
        <Kpi testid="cad-kpi-projection" icon={BarChart3} tone={ref?.projection_pct >= 0 ? "green" : "amber"} label="Projection fin de mois" value={fmtEur(ref?.projection)}
             lines={[{ text: `${fmtPctS(ref?.projection_pct)} vs objectif`, cls: signCls(ref?.projection_pct) + " font-semibold" }, { text: ref ? `${ref.days_left} j restants · ${ref.label}` : "" }]}/>
        <Kpi testid="cad-kpi-perimetre" icon={Target} tone="blue" label="Périmètre" value={`${s.n_corners || 0} corners`}
             lines={[{ text: `${s.n_days || 0} jours · ${s.date_from ? s.date_from.slice(8) + "/" + s.date_from.slice(5, 7) : ""} → ${s.date_to ? s.date_to.slice(8) + "/" + s.date_to.slice(5, 7) : ""}` }]}/>
      </div>

      <ResteAFaire refm={ref}/>

      <CaDetailTable rows={rows} group={group} setGroup={setGroup} total={s.realise || 0}/>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WeekdayWeight weekday={data?.weekday || []}/>
        <WeeksWeight refm={ref}/>
        <Contributions contributions={data?.contributions}/>
        <Day14h day14={data?.day14}/>
        <ProjectionChart refm={ref}/>
        <ExtraStats by_dr={data?.by_dr || []} weekday={data?.weekday || []} stats={data?.stats}/>
        <div className="lg:col-span-3"><WeatherImpact impact={impact} status={wxStatus} onSync={syncWeather} canSync={canSync} syncing={syncing}/></div>
      </div>

      {loading && <div className="fixed bottom-4 right-4 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg animate-pulse">Chargement…</div>}
    </div>
  );
}
